/**
 * INR ↔ K-INR conversion for the UI.
 *
 * The backend stores every monetary value in K-INR (thousand rupees), but
 * users naturally type in plain rupees. Every amount input in the UI
 * accepts INR and converts to K-INR via these helpers before sending to
 * the API.
 *
 * The conversion is exact: multiply by 0.001 and round half-even to 3dp,
 * matching the server-side `inrToKinr` in `src/money/decimal.ts`.
 */

/**
 * Round half-even ("banker's rounding") to `scale` decimal places.
 * Matches the behaviour of the backend Decimal wrapper at 3dp so
 * front-end preview values and server-side storage agree byte-for-byte.
 */
function roundHalfEven(value: number, scale: number): number {
  if (!Number.isFinite(value)) return NaN;
  const factor = Math.pow(10, scale);
  const scaled = value * factor;
  const floor = Math.floor(scaled);
  const diff = scaled - floor;
  let rounded: number;
  if (diff > 0.5) rounded = floor + 1;
  else if (diff < 0.5) rounded = floor;
  else rounded = floor % 2 === 0 ? floor : floor + 1;
  return rounded / factor;
}

/** Parse a user-typed INR string. Returns NaN for invalid input. */
export function parseInr(input: string): number {
  const trimmed = input.trim();
  if (trimmed.length === 0) return NaN;
  // Allow optional leading sign, digits, one decimal point, thousand-commas.
  if (!/^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/.test(trimmed)) return NaN;
  const stripped = trimmed.replace(/,/g, '');
  const n = parseFloat(stripped);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Convert a user-typed INR string to a K-INR string suitable for API
 * payloads. Returns `null` if the input is empty or invalid.
 *
 * Example: `"60000"` → `"60.000"`; `"60.5"` → `"0.061"` (half-even of 0.0605).
 */
export function inrToKinrString(input: string): string | null {
  const n = parseInr(input);
  if (!Number.isFinite(n)) return null;
  const kinr = roundHalfEven(n / 1000, 3);
  return kinr.toFixed(3);
}

/**
 * Format an INR amount as a readable K-INR preview string, e.g. "60.000 K-INR".
 * Returns `null` if input is invalid or empty.
 */
export function inrToKinrPreview(input: string): string | null {
  const k = inrToKinrString(input);
  return k === null ? null : `${k} K-INR`;
}
